import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi, type AsyncResult } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate, formatNumber } from '@/i18n/format';
import type { InventoryRow, Page } from '@/api/types';
import { StockSummaryStrip } from '@/components/StockSummaryStrip';
import { useAuth } from '@/auth/AuthContext';
import { FoundStockPanel } from './FoundStock';

const LIMIT = 50;

type Row = InventoryRow & { id: string };

function withRowId(result: AsyncResult<Page<InventoryRow>> & { reload: () => void }): AsyncResult<Page<Row>> & { reload: () => void } {
  if (result.status !== 'success') return result;
  return {
    status: 'success',
    reload: result.reload,
    data: {
      total: result.data.total,
      rows: result.data.rows.map((r) => ({ ...r, id: `${r.sku_id}:${r.owner_id}:${r.location_id}` })),
    },
  };
}

/**
 * Part B / "never a summed row blending seller ownership": the grouping key
 * server-side is (warehouse, location, sku, owner, lot) so a client filter
 * here can never accidentally aggregate two owners' stock into one row.
 */
export function InventoryListPage(): JSX.Element {
  const [sku, setSku] = useState('');
  const [offset, setOffset] = useState(0);
  const result = useApi(() => endpoints.inventory({ sku: sku || undefined, limit: LIMIT, offset }), [sku, offset]);
  const { t, locale } = useLanguage();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('INVENTORY.WRITE');
  const [foundOpen, setFoundOpen] = useState(false);

  const columns: readonly Column<Row>[] = [
    { key: 'sku', header: t('inventory.colSku'), render: (r) => `${r.sku_code} — ${r.sku_name}` },
    { key: 'owner', header: t('inventory.colSeller'), render: (r) => r.owner_name },
    { key: 'location', header: t('inventory.colLocation'), render: (r) => `${r.warehouse_name} / ${r.location_path}` },
    { key: 'bucket', header: t('inventory.colBucket'), render: (r) => t(`inventory.bucket.${r.stock_bucket}`) },
    { key: 'on_hand', header: t('inventory.colOnHand'), render: (r) => formatNumber(r.qty_on_hand, locale), align: 'right' },
    { key: 'reserved', header: t('inventory.colReserved'), render: (r) => formatNumber(r.qty_reserved, locale), align: 'right' },
    { key: 'available', header: t('inventory.colAvailable'), render: (r) => formatNumber(r.qty_available, locale), align: 'right' },
    { key: 'last_movement', header: t('inventory.colLastMovement'), render: (r) => formatDate(r.last_movement, locale) },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('inventory.title')}</h1>
        {canWrite && !foundOpen && (
          <button type="button" onClick={() => setFoundOpen(true)}>
            {t('found.action')}
          </button>
        )}
      </div>
      {canWrite && foundOpen && (
        <FoundStockPanel
          onClose={() => setFoundOpen(false)}
          onDone={() => {
            setFoundOpen(false);
            result.reload();
          }}
        />
      )}
      <div className="filters">
        <input
          id="sku-filter"
          placeholder={t('inventory.searchPlaceholder')}
          aria-label={t('inventory.searchAriaLabel')}
          value={sku}
          onChange={(e) => {
            setSku(e.target.value);
            setOffset(0);
          }}
        />
      </div>
      {result.status === 'success' && result.data.summary && <StockSummaryStrip summary={result.data.summary} />}
      <DataTable
        result={withRowId(result)}
        columns={columns}
        getRowHref={(r) => `/inventory/${r.sku_id}/${r.owner_id}`}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={t('inventory.emptyTitle')}
      />
    </div>
  );
}
